import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Form, Image, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignature, faPen, faKeyboard, faEraser } from '@fortawesome/free-solid-svg-icons';
import SignaturePad from 'signature_pad';

const CANVAS_HEIGHT = 120;
const CANVAS_WIDTH = 400;

// Crop canvas to content bounds (removes whitespace)
const cropCanvasToContent = (sourceCanvas, padding = 5) => {
  const ctx = sourceCanvas.getContext('2d');
  const width = sourceCanvas.width;
  const height = sourceCanvas.height;
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  let minX = width, minY = height, maxX = 0, maxY = 0;
  let hasContent = false;

  // Find bounds of non-transparent pixels
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > 0) {
        hasContent = true;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (!hasContent) return sourceCanvas;

  // Add padding
  minX = Math.max(0, minX - padding);
  minY = Math.max(0, minY - padding);
  maxX = Math.min(width - 1, maxX + padding);
  maxY = Math.min(height - 1, maxY + padding);

  const cropWidth = maxX - minX + 1;
  const cropHeight = maxY - minY + 1;

  // Create cropped canvas
  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = cropWidth;
  croppedCanvas.height = cropHeight;
  const croppedCtx = croppedCanvas.getContext('2d');

  croppedCtx.drawImage(
    sourceCanvas,
    minX, minY, cropWidth, cropHeight,
    0, 0, cropWidth, cropHeight
  );

  return croppedCanvas;
};

const MODE_OPTIONS = [
  { value: 'draw', label: 'Draw Signature', icon: faPen },
  { value: 'type', label: 'Type Signature', icon: faKeyboard },
];

const SignatureInput = ({ onSave, onChange, currentSignatureUrl, disabled, isLoading, drawModeReadOnly = false, showSaveButton = true }) => {
  const [mode, setMode] = useState('draw');
  const [typedName, setTypedName] = useState('');
  const [hasDrawing, setHasDrawing] = useState(false);

  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  const typeCanvasRef = useRef(null);

  // Initialize signature pad when mode is 'draw'
  useEffect(() => {
    if (mode === 'draw' && canvasRef.current) {
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
        signaturePadRef.current = null;
      }

      const canvas = canvasRef.current;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);

      canvas.width = CANVAS_WIDTH * ratio;
      canvas.height = CANVAS_HEIGHT * ratio;
      canvas.style.width = `${CANVAS_WIDTH}px`;
      canvas.style.height = `${CANVAS_HEIGHT}px`;

      const ctx = canvas.getContext('2d');
      ctx.scale(ratio, ratio);

      signaturePadRef.current = new SignaturePad(canvas, {
        backgroundColor: 'rgba(0, 0, 0, 0)',
        penColor: 'rgb(0, 0, 0)',
        minWidth: 1,
        maxWidth: 3,
      });

      signaturePadRef.current.addEventListener('endStroke', () => {
        setHasDrawing(!signaturePadRef.current.isEmpty());
      });
    }

    return () => {
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
        signaturePadRef.current = null;
      }
    };
  }, [mode]);

  // Render typed signature to canvas
  const renderTypedSignature = useCallback(() => {
    if (!typeCanvasRef.current) return;

    const canvas = typeCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    canvas.width = CANVAS_WIDTH * ratio;
    canvas.height = CANVAS_HEIGHT * ratio;
    canvas.style.width = `${CANVAS_WIDTH}px`;
    canvas.style.height = `${CANVAS_HEIGHT}px`;

    ctx.scale(ratio, ratio);
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Exit after clearing if no text to render
    if (!typedName.trim()) return;

    let fontSize = 56;
    ctx.font = `${fontSize}px "Great Vibes"`;
    let textWidth = ctx.measureText(typedName).width;

    while (textWidth > CANVAS_WIDTH - 30 && fontSize > 20) {
      fontSize -= 4;
      ctx.font = `${fontSize}px "Great Vibes"`;
      textWidth = ctx.measureText(typedName).width;
    }

    ctx.fillStyle = 'black';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    // Offset slightly down to visually center cursive font (ascenders make it appear high)
    ctx.fillText(typedName, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 8);
  }, [typedName]);

  useEffect(() => {
    if (mode === 'type') {
      renderTypedSignature();
    }
  }, [mode, typedName, renderTypedSignature]);

  // Build the current signature as a PNG File (or null when empty) and hand it
  // back via the callback. Used by both the in-tab Save and the onChange notifier.
  const buildSignatureFile = useCallback((cb) => {
    if (mode === 'draw' && signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const croppedCanvas = cropCanvasToContent(canvasRef.current);
      croppedCanvas.toBlob((blob) => {
        cb(new File([blob], 'signature.png', { type: 'image/png' }), 'drawn');
      }, 'image/png');
    } else if (mode === 'type' && typedName.trim() && typeCanvasRef.current) {
      const croppedCanvas = cropCanvasToContent(typeCanvasRef.current);
      croppedCanvas.toBlob((blob) => {
        cb(new File([blob], 'signature.png', { type: 'image/png' }), 'typed');
      }, 'image/png');
    } else {
      cb(null, null);
    }
  }, [mode, typedName]);

  // When a parent drives saving (onChange provided, e.g. a modal with a single
  // footer Save), emit the current signature whenever it changes so the parent
  // can persist it on its own Save and warn about it being unsaved. Runs after
  // the render effect above, so the type-mode canvas is already drawn.
  // (onChange intentionally omitted from deps — parents pass an inline fn; we
  // only want to re-emit on actual content change, not on every render.)
  useEffect(() => {
    if (!onChange) return;
    buildSignatureFile((file, type) => onChange(file, type));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, typedName, hasDrawing, buildSignatureFile]);

  const handleModeChange = (e) => {
    const newMode = e.target.value;
    setMode(newMode);
    setTypedName('');
    setHasDrawing(false);
  };

  const handleClear = () => {
    if (mode === 'draw' && signaturePadRef.current) {
      signaturePadRef.current.clear();
      setHasDrawing(false);
    } else if (mode === 'type') {
      setTypedName('');
    }
  };

  const handleSave = () => {
    buildSignatureFile((file, type) => {
      if (file && onSave) onSave(file, type);
    });
  };

  const canSave = () => {
    if (mode === 'draw') return hasDrawing;
    if (mode === 'type') return !!typedName.trim();
    return false;
  };

  return (
    <div className="signature-input">
      <Row>
        {/* Left side - Signing options */}
        <Col lg={6} className="mb-3 mb-lg-0">
          <Form.Group className="mb-3 text-center">
            <Form.Label>Signing Option</Form.Label>
            <Form.Select
              value={mode}
              onChange={handleModeChange}
              disabled={disabled}
              size="sm"
              className="mx-auto"
              style={{ maxWidth: `${CANVAS_WIDTH}px` }}
            >
              {MODE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Draw Mode */}
          {mode === 'draw' && (
            <>
              <div
                className="border rounded mb-2 mx-auto"
                style={{
                  background: '#fff',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)',
                  position: 'relative',
                  width: 'fit-content',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  opacity: drawModeReadOnly ? 0.6 : 1,
                }}
              >
                <canvas
                  ref={canvasRef}
                  style={{
                    display: 'block',
                    touchAction: 'none',
                    cursor: disabled || drawModeReadOnly ? 'not-allowed' : 'crosshair',
                    pointerEvents: drawModeReadOnly ? 'none' : 'auto',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '10%',
                    right: '10%',
                    borderBottom: '1px dashed #ccc',
                    pointerEvents: 'none',
                  }}
                />
                {!hasDrawing && !drawModeReadOnly && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: '#bbb',
                      pointerEvents: 'none',
                      textAlign: 'center',
                    }}
                  >
                    <FontAwesomeIcon icon={faPen} className="me-2" />
                    <small>Sign here</small>
                  </div>
                )}
              </div>
              {drawModeReadOnly && (
                <p className="text-muted small text-center mb-3">
                  For Provider only
                </p>
              )}
            </>
          )}

          {/* Type Mode */}
          {mode === 'type' && (
            <div className="mb-3 d-flex flex-column align-items-center">
              <Form.Control
                type="text"
                placeholder="Type your name..."
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                disabled={disabled}
                className="mb-2"
                size="sm"
                style={{ maxWidth: `${CANVAS_WIDTH}px`, width: '100%' }}
              />
              <div
                className="border rounded"
                style={{
                  background: '#fff',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)',
                  width: `${CANVAS_WIDTH}px`,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <canvas
                  ref={typeCanvasRef}
                  style={{ display: 'block', width: '100%', height: 'auto' }}
                />
                {!typedName.trim() && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: '#bbb',
                      pointerEvents: 'none',
                    }}
                  >
                    <small>Preview</small>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="d-flex gap-2 justify-content-center">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleClear}
              disabled={disabled || (mode === 'draw' && drawModeReadOnly)}
            >
              <FontAwesomeIcon icon={faEraser} className="me-1" />
              Clear
            </Button>
            {showSaveButton && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={disabled || !canSave() || (mode === 'draw' && drawModeReadOnly)}
              >
                <FontAwesomeIcon icon={faSignature} className="me-1" />
                Save
              </Button>
            )}
          </div>
        </Col>

        {/* Right side - Current signature */}
        <Col lg={6}>
          <Form.Label>Current Signature</Form.Label>
          <div
            className="border rounded p-3 text-center"
            style={{
              background: '#f8f9fa',
              minHeight: `${CANVAS_HEIGHT}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {currentSignatureUrl ? (
              <Image
                src={currentSignatureUrl}
                alt="Current Signature"
                style={{ maxWidth: '100%', maxHeight: `${CANVAS_HEIGHT}px` }}
              />
            ) : (
              <span className="text-muted small">No signature saved</span>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default SignatureInput;
